import { getSeoEntryByRoute, SeoRegistryEntry } from "./seoRegistry";
import { getRawSeoConfig, SeoPageMetadata } from "./seoConfig";
import { getLocationBySlug, isLocationQualified, getCountryBySlug, getAllSupportedStates } from "./locationRegistry";
import { validateSeoContentRules } from "../config/seoContentRules";
import { generatePreRenderedBodyHtml } from "./ssrHtmlGenerator";

export interface EligibilityResult {
  isEligible: boolean;
  indexingStatus: "index, follow" | "noindex, follow" | "noindex, nofollow";
  reasons: string[];
}

const PRIVATE_AND_AUTH_PREFIXES = [
  "/dashboard",
  "/app",
  "/settings",
  "/saved",
  "/account",
  "/login",
  "/signup",
  "/billing",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/auth/callback",
  "/signup-success",
  "/login-success",
  "/billing/success",
  "/billing/cancel",
];

/**
 * Centralized SEO Indexability and Eligibility Engine for BizNearYou.
 * Strictly determines whether a given URL route deserves to be indexed in Google/search engines
 * and included in XML sitemaps.
 */
export function isSeoIndexEligible(route: string): EligibilityResult {
  const reasons: string[] = [];
  const cleanRoute = route.split("?")[0].split("#")[0].toLowerCase() || "/";

  // 1. Private and Auth Routes Check
  const isPrivateOrAuth = PRIVATE_AND_AUTH_PREFIXES.some(
    (prefix) => cleanRoute === prefix || cleanRoute.startsWith(prefix + "/")
  );
  if (isPrivateOrAuth) {
    reasons.push("Private user or authentication route.");
    return {
      isEligible: false,
      indexingStatus: "noindex, follow",
      reasons,
    };
  }

  // 2. Query Parameters and Shared Business URLs
  if (route.includes("sharedBusinessId=") || route.includes("demo=") || route.includes("redirect=")) {
    reasons.push("URL contains transient session or share parameter.");
    return {
      isEligible: false,
      indexingStatus: "noindex, follow",
      reasons,
    };
  }

  // 3. Location / Region / Country Route Validation
  const locMatch = cleanRoute.match(/^\/(jobs|businesses-hiring|internships|locations)\/([a-z0-9-]+)$/);
  if (locMatch) {
    const slug = locMatch[2];
    if (slug !== "locations") {
      const country = getCountryBySlug(slug);
      const isState = getAllSupportedStates().some(
        (s) => (s.regionSlug || s.stateName.toLowerCase().replace(/\s+/g, "-")) === slug
      );
      if (!country && !isState) {
        const loc = getLocationBySlug(slug);
        if (!loc) {
          reasons.push(`Location slug "${slug}" not found in registry.`);
          return { isEligible: false, indexingStatus: "noindex, follow", reasons };
        }
        if (!isLocationQualified(loc)) {
          reasons.push(
            `Location "${loc.cityName}" does not meet quality threshold (businesses: ${loc.businessCount}/10, categories: ${loc.relevantCategoryCount}/5, coverage: ${loc.dataCoverageScore}/70).`
          );
          return { isEligible: false, indexingStatus: "noindex, follow", reasons };
        }
      }
    }
  }

  // 4. Registry and Metadata Validation
  const regEntry = getSeoEntryByRoute(cleanRoute);
  const seoConfig = getRawSeoConfig(cleanRoute);

  if (regEntry && regEntry.publicationStatus === "disabled") {
    reasons.push("Publication status is set to disabled in registry.");
    return { isEligible: false, indexingStatus: "noindex, follow", reasons };
  }

  if (regEntry && regEntry.indexingStatus.includes("noindex")) {
    reasons.push("Explicitly marked as noindex in registry.");
    return { isEligible: false, indexingStatus: "noindex, follow", reasons };
  }

  if (seoConfig.robots && seoConfig.robots.includes("noindex")) {
    reasons.push("Explicitly marked as noindex in page metadata config.");
    return { isEligible: false, indexingStatus: "noindex, follow", reasons };
  }

  // 5. Title & Description Quality Check
  const title = seoConfig.title || regEntry?.title || "";
  const description = seoConfig.description || regEntry?.metaDescription || "";

  if (!title || title.length < 15) {
    reasons.push("Title tag is missing or too short (<15 chars).");
  }

  if (!description || description.length < 40) {
    reasons.push("Meta description is missing or too short (<40 chars).");
  }

  // 6. Pre-rendered HTML Body Check
  const htmlBody = generatePreRenderedBodyHtml(cleanRoute);
  const plainText = htmlBody.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const wordCount = plainText ? plainText.split(" ").length : 0;

  if (wordCount < 25) {
    reasons.push(`Pre-rendered HTML word count is too low (${wordCount} words < 25 words).`);
  }

  // 7. Editorial Content Rules Validation
  const titleValidation = validateSeoContentRules(title);
  const descValidation = validateSeoContentRules(description);
  const bodyValidation = validateSeoContentRules(plainText);

  const allViolations = [
    ...titleValidation.violations,
    ...descValidation.violations,
    ...bodyValidation.violations,
  ];

  const criticalErrors = allViolations.filter((v) => v.severity === "error");
  if (criticalErrors.length > 0) {
    criticalErrors.forEach((v) => reasons.push(`Content Rule Failure: ${v.message}`));
  }

  const isEligible = reasons.length === 0;
  return {
    isEligible,
    indexingStatus: isEligible ? "index, follow" : "noindex, follow",
    reasons,
  };
}
